/** Demo Mode only - reusable expandable Profit & Loss table. Works at company level (pass
 * `contracts`, the totals are summed via sumLineItems so they always reconcile with
 * contract-level data) or single-contract/site level (pass a single-entry `contracts` array).
 * Columns: Actual, Budget, Current forecast, Variance to budget (£ and %), Prior year. */

import { Fragment, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Typography, useTheme,
} from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  FinanceContract, PnLLineItems, computeTotals, sumLineItems, CURRENT_PERIOD_INDEX,
} from '../../../mocks/data/financeData';
import { applyRoundAndFormat } from '../../../utils';

const scaleLineItems = (items: PnLLineItems, factor: number): PnLLineItems => Object.fromEntries(
  Object.entries(items).map(([key, value]) => [key, value * factor]),
) as unknown as PnLLineItems;

interface PnLRowDef {
  key: string;
  label: string;
  actual: number;
  budget: number;
  currentForecast: number;
  priorYear: number;
  isPercentage?: boolean;
  bold?: boolean;
}

interface PnLGroupDef {
  key: string;
  label: string;
  rows: PnLRowDef[];
  total: PnLRowDef;
}

export interface ProfitAndLossTableProps {
  contracts: FinanceContract[];
  view: 'ytd' | 'fullYear';
  currencyCode?: string;
}

const ProfitAndLossTable = ({ contracts, view, currencyCode }: ProfitAndLossTableProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ income: true, costOfSales: true, labour: true, opex: false });

  // lazy: YTD is approximated by scaling annualised scenario totals by elapsed-months/12
  // rather than modelling a truly independent monthly P&L per line item - upgrade path is
  // per-month line-item actuals once a real financial feed exists.
  const factor = view === 'ytd' ? (CURRENT_PERIOD_INDEX + 1) / 12 : 1;

  const summed = (scenario: 'actual' | 'budget' | 'currentForecast' | 'priorYear') => scaleLineItems(
    sumLineItems(contracts.map((c) => c.scenarios[scenario])),
    factor,
  );

  const actualItems = summed('actual');
  const budgetItems = summed('budget');
  const forecastItems = summed('currentForecast');
  const priorYearItems = summed('priorYear');

  const actualTotals = computeTotals(actualItems);
  const budgetTotals = computeTotals(budgetItems);
  const forecastTotals = computeTotals(forecastItems);
  const priorYearTotals = computeTotals(priorYearItems);

  const row = (key: string, label: string, field: keyof PnLLineItems, isPercentage = false, bold = false): PnLRowDef => ({
    key,
    label,
    actual: actualItems[field],
    budget: budgetItems[field],
    currentForecast: forecastItems[field],
    priorYear: priorYearItems[field],
    isPercentage,
    bold,
  });

  const groups: PnLGroupDef[] = [
    {
      key: 'income',
      label: t('financePnlIncome', 'Income'),
      rows: [
        row('foodSales', t('financePnlFoodSales', 'Food sales'), 'foodSales'),
        row('hospitalitySales', t('financePnlHospitalitySales', 'Hospitality/event sales'), 'hospitalitySales'),
        row('managementFees', t('financePnlManagementFees', 'Management fees'), 'managementFees'),
        row('clientSubsidies', t('financePnlClientSubsidies', 'Client subsidies'), 'clientSubsidies'),
        row('otherIncome', t('financePnlOtherIncome', 'Other income'), 'otherIncome'),
      ],
      total: {
        key: 'totalRevenue', label: t('financePnlTotalRevenue', 'Total revenue'), actual: actualTotals.totalRevenue, budget: budgetTotals.totalRevenue, currentForecast: forecastTotals.totalRevenue, priorYear: priorYearTotals.totalRevenue, bold: true,
      },
    },
    {
      key: 'costOfSales',
      label: t('financePnlCostOfSales', 'Cost of sales'),
      rows: [
        row('foodAndBeverage', t('financePnlFoodAndBeverage', 'Food & beverage'), 'foodAndBeverage'),
        row('consumables', t('financePnlConsumables', 'Consumables'), 'consumables'),
        row('waste', t('financePnlWaste', 'Waste'), 'waste'),
        row('supplierRebates', t('financePnlSupplierRebates', 'Supplier rebates/credits'), 'supplierRebates'),
      ],
      total: {
        key: 'totalCostOfSales', label: t('financePnlTotalCostOfSales', 'Total cost of sales'), actual: actualTotals.totalCostOfSales, budget: budgetTotals.totalCostOfSales, currentForecast: forecastTotals.totalCostOfSales, priorYear: priorYearTotals.totalCostOfSales, bold: true,
      },
    },
    {
      key: 'labour',
      label: t('financePnlLabour', 'Labour'),
      rows: [
        row('salariesAndWages', t('financePnlSalariesAndWages', 'Salaries & wages'), 'salariesAndWages'),
        row('agencyLabour', t('financePnlAgencyLabour', 'Agency labour'), 'agencyLabour'),
        row('overtime', t('financePnlOvertime', 'Overtime'), 'overtime'),
        row('employerCosts', t('financePnlEmployerCosts', 'Employer costs'), 'employerCosts'),
      ],
      total: {
        key: 'totalLabour', label: t('financePnlTotalLabour', 'Total labour'), actual: actualTotals.totalLabour, budget: budgetTotals.totalLabour, currentForecast: forecastTotals.totalLabour, priorYear: priorYearTotals.totalLabour, bold: true,
      },
    },
    {
      key: 'opex',
      label: t('financePnlOpex', 'Operating expenses'),
      rows: [
        row('equipment', t('financePnlEquipment', 'Equipment'), 'equipment'),
        row('cleaning', t('financePnlCleaning', 'Cleaning'), 'cleaning'),
        row('utilities', t('financePnlUtilities', 'Utilities'), 'utilities'),
        row('travel', t('financePnlTravel', 'Travel'), 'travel'),
        row('training', t('financePnlTraining', 'Training'), 'training'),
        row('centralCharges', t('financePnlCentralCharges', 'Central charges'), 'centralCharges'),
        row('otherOpex', t('financePnlOtherOpex', 'Other'), 'otherOpex'),
      ],
      total: {
        key: 'totalOpex', label: t('financePnlTotalOpex', 'Total operating expenses'), actual: actualTotals.totalOpex, budget: budgetTotals.totalOpex, currentForecast: forecastTotals.totalOpex, priorYear: priorYearTotals.totalOpex, bold: true,
      },
    },
  ];

  const resultRows: PnLRowDef[] = [
    {
      key: 'grossProfit', label: t('financePnlGrossProfit', 'Gross profit'), actual: actualTotals.grossProfit, budget: budgetTotals.grossProfit, currentForecast: forecastTotals.grossProfit, priorYear: priorYearTotals.grossProfit, bold: true,
    },
    {
      key: 'grossProfitPercentage', label: t('financePnlGrossProfitPercentage', 'Gross profit %'), actual: actualTotals.grossProfitPercentage, budget: budgetTotals.grossProfitPercentage, currentForecast: forecastTotals.grossProfitPercentage, priorYear: priorYearTotals.grossProfitPercentage, isPercentage: true,
    },
    {
      key: 'contribution', label: t('financePnlContribution', 'Contribution'), actual: actualTotals.contribution, budget: budgetTotals.contribution, currentForecast: forecastTotals.contribution, priorYear: priorYearTotals.contribution, bold: true,
    },
    {
      key: 'allocatedOverhead', label: t('financePnlAllocatedOverhead', 'Allocated overhead'), actual: actualTotals.allocatedOverhead, budget: budgetTotals.allocatedOverhead, currentForecast: forecastTotals.allocatedOverhead, priorYear: priorYearTotals.allocatedOverhead,
    },
    {
      key: 'ebit', label: t('financePnlEbit', 'EBIT'), actual: actualTotals.ebit, budget: budgetTotals.ebit, currentForecast: forecastTotals.ebit, priorYear: priorYearTotals.ebit, bold: true,
    },
    {
      key: 'ebitMargin', label: t('financePnlEbitMargin', 'EBIT margin'), actual: actualTotals.ebitMarginPercentage, budget: budgetTotals.ebitMarginPercentage, currentForecast: forecastTotals.ebitMarginPercentage, priorYear: priorYearTotals.ebitMarginPercentage, isPercentage: true, bold: true,
    },
  ];

  const formatValue = (value: number, isPercentage?: boolean) => {
    if (isPercentage) return `${value.toFixed(1)}%`;
    if (value < 0) return `(${applyRoundAndFormat(Math.abs(value), currencyCode)})`;
    return applyRoundAndFormat(value, currencyCode);
  };

  const varianceToBudget = (currentForecast: number, budget: number) => {
    const amount = currentForecast - budget;
    const percentage = budget !== 0 ? (amount / Math.abs(budget)) * 100 : 0;
    return { amount, percentage };
  };

  const renderRow = (r: PnLRowDef, indent = false) => {
    const variance = varianceToBudget(r.currentForecast, r.budget);
    return (
      <TableRow key={r.key}>
        <TableCell sx={{ pl: indent ? 8 : 4, fontWeight: r.bold ? 600 : 400 }}>{r.label}</TableCell>
        <TableCell align="right"
          sx={{ fontWeight: r.bold ? 600 : 400 }}>{formatValue(r.actual, r.isPercentage)}</TableCell>
        <TableCell align="right"
          sx={{ fontWeight: r.bold ? 600 : 400 }}>{formatValue(r.budget, r.isPercentage)}</TableCell>
        <TableCell align="right"
          sx={{ fontWeight: r.bold ? 600 : 400 }}>{formatValue(r.currentForecast, r.isPercentage)}</TableCell>
        <TableCell align="right"
          sx={{ color: variance.amount < 0 ? theme.palette.error.main : theme.palette.success.main }}>
          {r.isPercentage
            ? `${variance.amount >= 0 ? '+' : '-'}${Math.abs(variance.amount).toFixed(1)}pts`
            : `${variance.amount < 0 ? '(' : '+'}${applyRoundAndFormat(Math.abs(variance.amount), currencyCode)}${variance.amount < 0 ? ')' : ''} (${variance.percentage >= 0 ? '+' : ''}${variance.percentage.toFixed(1)}%)`}
        </TableCell>
        <TableCell align="right">{formatValue(r.priorYear, r.isPercentage)}</TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('financePnlLine', 'P&L line')}</TableCell>
            <TableCell align="right">{t('financePnlActual', 'Actual')}</TableCell>
            <TableCell align="right">{t('financePnlBudget', 'Budget')}</TableCell>
            <TableCell align="right">{t('financePnlCurrentForecast', 'Current forecast')}</TableCell>
            <TableCell align="right">{t('financePnlVarianceToBudget', 'Variance to budget')}</TableCell>
            <TableCell align="right">{t('financePnlPriorYear', 'Prior year')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <Fragment key={group.key}>
              <TableRow sx={{ backgroundColor: theme.palette.custom.gray[50] }}>
                <TableCell colSpan={6}
                  sx={{ py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => setExpanded((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                      aria-label={`toggle-${group.key}`}
                    >
                      {expanded[group.key] ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                    </IconButton>
                    <Typography variant="textSM"
                      fontWeight={600}>{group.label}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
              {expanded[group.key] && group.rows.map((r) => renderRow(r, true))}
              {renderRow(group.total)}
            </Fragment>
          ))}
          <TableRow sx={{ backgroundColor: theme.palette.custom.gray[100] }}>
            <TableCell colSpan={6}>
              <Typography variant="textSM"
                fontWeight={600}>{t('financePnlResult', 'Result')}</Typography>
            </TableCell>
          </TableRow>
          {resultRows.map((r) => renderRow(r))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProfitAndLossTable;
